import React, { useEffect, useState } from 'react';
import axios from 'axios';
import OrganizationLayout from './OrganizationLayout';
import { useOrgData, API, authHeaders } from './useOrgData';
import { BASE_PRICE, SEAT_PRICE, monthlyCost, extraTeammates } from '../../utils/orgPricing';

const Line = ({ label, value, testId, strong }) => (
  <div className={`flex justify-between py-2 ${strong ? 'border-t border-gray-200 mt-1 pt-3' : ''}`}>
    <span className={strong ? 'font-semibold text-gray-900' : 'text-gray-600'}>{label}</span>
    <span className={strong ? 'text-lg font-bold text-gray-900' : 'font-medium text-gray-900'} data-testid={testId}>{value}</span>
  </div>
);

const Breakdown = ({ seats, prefix }) => {
  const extra = extraTeammates(seats);
  return (
    <div className="text-sm">
      <Line label="Base plan · you + 1 teammate" value={`$${BASE_PRICE}`} testId={`${prefix}-base`} />
      <Line label={`Extra teammates · ${extra} × $${SEAT_PRICE}`} value={`$${extra * SEAT_PRICE}`} testId={`${prefix}-extra`} />
      <Line label="Total per month" value={`$${monthlyCost(seats)}`} testId={`${prefix}-total`} strong />
    </div>
  );
};

const Calculator = ({ currentTeammates }) => {
  const [teammates, setTeammates] = useState(currentTeammates);
  useEffect(() => setTeammates(currentTeammates), [currentTeammates]);
  const seats = teammates + 1;
  const diff = monthlyCost(seats) - monthlyCost(currentTeammates + 1);
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="billing-calculator">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Try a different team size</h2>
      <p className="text-sm text-gray-600 mb-4">See what you'd pay each month before you invite anyone.</p>
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => setTeammates((n) => Math.max(0, n - 1))}
          className="w-10 h-10 rounded-full border border-gray-300 text-lg font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          data-testid="billing-calc-decrease"
          aria-label="Fewer teammates"
        >−</button>
        <div className="text-center min-w-[7rem]">
          <p className="text-3xl font-bold text-gray-900" data-testid="billing-calc-teammates">{teammates}</p>
          <p className="text-xs text-gray-500">teammate{teammates === 1 ? '' : 's'} + you</p>
        </div>
        <button
          onClick={() => setTeammates((n) => Math.min(500, n + 1))}
          className="w-10 h-10 rounded-full border border-gray-300 text-lg font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          data-testid="billing-calc-increase"
          aria-label="More teammates"
        >+</button>
      </div>
      <Breakdown seats={seats} prefix="billing-calc" />
      {diff !== 0 && (
        <p className={`mt-3 text-sm font-medium ${diff > 0 ? 'text-indigo-700' : 'text-emerald-700'}`} data-testid="billing-calc-diff">
          {diff > 0 ? `+$${diff}` : `−$${Math.abs(diff)}`}/month compared with today
        </p>
      )}
    </section>
  );
};

const OrganizationBillingPage = () => {
  const { org, members, loading, canManage, isOwner } = useOrgData();
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!org || !canManage) return;
    axios.get(`${API}/organizations/${org.id}/invitations`, { headers: authHeaders() })
      .then((r) => setPending((r.data || []).length))
      .catch(() => setPending(0));
  }, [org, canManage]);

  const openStripe = async (kind) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/organizations/${org.id}/${kind}`,
        { origin_url: window.location.origin },
        { headers: authHeaders() }
      );
      window.location.href = data.portal_url || data.checkout_url;
    } catch (err) {
      alert(err?.response?.data?.detail || 'Billing is not available right now.');
      setBusy(false);
    }
  };

  if (loading || !org) return null;
  if (!canManage) {
    return (
      <OrganizationLayout org={org} active="billing" canManage={false}>
        <p className="bg-white rounded-2xl shadow-sm p-6 text-gray-700" data-testid="org-billing-restricted">
          Only the workspace owner or an admin can view billing.
        </p>
      </OrganizationLayout>
    );
  }
  const seats = org.seat_count || members.length || 1;
  const trialEnds = org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString() : null;

  return (
    <OrganizationLayout org={org} active="billing" canManage={canManage}>
      <div data-testid="org-billing-page">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Billing</h1>
        <p className="text-sm text-gray-500 mb-6">
          $10/month includes you + 1 teammate. Each extra teammate adds $5/month.
        </p>
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="billing-current">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Your plan today</h2>
            <p className="text-sm text-gray-600 mb-3">
              {org.subscription_status === 'trialing' && trialEnds
                ? `Free trial until ${trialEnds}`
                : org.subscription_status.charAt(0).toUpperCase() + org.subscription_status.slice(1)}
              {' · '}{seats} seat{seats === 1 ? '' : 's'}
            </p>
            <Breakdown seats={seats} prefix="billing-current" />
            {pending > 0 && (
              <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="billing-pending-note">
                {pending} pending invitation{pending === 1 ? '' : 's'} — once accepted: ${monthlyCost(seats + pending)}/month
              </p>
            )}
            {isOwner && (
              <button
                onClick={() => openStripe(org.stripe_subscription_id ? 'billing-portal' : 'checkout')}
                disabled={busy}
                className="mt-4 w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-2.5 rounded-lg font-medium disabled:opacity-60 hover:from-indigo-600 hover:to-purple-700 transition-colors"
                data-testid="billing-manage-btn"
              >
                {busy ? 'Opening…' : org.stripe_subscription_id ? 'Manage payment & invoices' : 'Start subscription'}
              </button>
            )}
          </section>
          <Calculator currentTeammates={Math.max(0, seats - 1)} />
        </div>
      </div>
    </OrganizationLayout>
  );
};

export default OrganizationBillingPage;
